import { cloneHeights, heightAt, inBounds, SIZE, summitsOf } from './board.js';
import { legalActions } from './moves.js';
import type { Action, GameState, LogEntry, Piece, Team } from './types.js';

/**
 * Fog of war (experimental, `config.fog`). Each player sees only what their pieces can see; the
 * rest of the board shows the heights they last saw. The numbers below are playtest values.
 */

/** How many squares a piece sees along each straight line. */
export const FOG_VISION = 3;
/** How many squares the Lookout sees along each straight line: the whole board. */
export const LOOKOUT_SIGHT = SIZE - 1;
/** The Lookout sees as if it stood this many levels higher. */
export const LOOKOUT_EYE_BONUS = 2;

/** The four straight lines from a piece: ahead, behind, left and right. */
const SIGHT_LINES: readonly (readonly [number, number])[] = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];

const NEIGHBOURS: readonly (readonly [number, number])[] = [
  ...SIGHT_LINES,
  [-1, -1],
  [-1, 1],
  [1, -1],
  [1, 1],
];

const IBEX_LANDINGS: readonly (readonly [number, number])[] = [
  [-2, -1],
  [-2, 1],
  [-1, -2],
  [-1, 2],
  [1, -2],
  [1, 2],
  [2, -1],
  [2, 1],
];

/** A 9×9 grid of booleans, indexed `[r][c]`. */
export type VisionGrid = boolean[][];

/**
 * Squares `team` can see on the real board. From each of its pieces:
 *
 * - its own square and the 8 squares around it;
 * - four straight lines (ahead, behind, left, right), 3 squares long, or the whole board for the
 *   Lookout. A line passes over lower and equal ground and stops at the first square higher than
 *   the viewer's eye (its own square, +2 for the Lookout): that square is seen, the ones behind
 *   it are not;
 * - for the Ibex, its 8 landing squares.
 *
 * Pieces never block sight. The summit and both Beacons are always visible.
 */
export function visionFor(
  state: Pick<GameState, 'heights' | 'pieces'> & {
    config?: Pick<GameState['config'], 'summitMode'>;
  },
  team: Team,
): VisionGrid {
  const seen = Array.from({ length: SIZE }, () => new Array<boolean>(SIZE).fill(false));
  const mark = (r: number, c: number) => {
    const row = seen[r];
    if (row && inBounds(r, c)) row[c] = true;
  };
  for (const s of summitsOf(state.config?.summitMode ?? 'center')) mark(s.r, s.c);
  for (const piece of state.pieces) {
    if (!piece.alive) continue;
    if (piece.type === 'B') mark(piece.r, piece.c);
    if (piece.team !== team) continue;
    mark(piece.r, piece.c);
    for (const [dr, dc] of NEIGHBOURS) mark(piece.r + dr, piece.c + dc);
    if (piece.type === 'X') {
      for (const [dr, dc] of IBEX_LANDINGS) mark(piece.r + dr, piece.c + dc);
    }
    const lookout = piece.type === 'L';
    const eye = heightAt(state.heights, piece.r, piece.c) + (lookout ? LOOKOUT_EYE_BONUS : 0);
    const range = lookout ? LOOKOUT_SIGHT : FOG_VISION;
    for (const [dr, dc] of SIGHT_LINES) {
      for (let d = 1; d <= range; d++) {
        const r = piece.r + dr * d;
        const c = piece.c + dc * d;
        if (!inBounds(r, c)) break;
        mark(r, c);
        if (heightAt(state.heights, r, c) > eye) break;
      }
    }
  }
  return seen;
}

function isSeen(grid: VisionGrid, r: number, c: number): boolean {
  return grid[r]?.[c] === true;
}

/**
 * What `team` is allowed to see. Without fog this is the whole state. With fog: enemy pieces out
 * of sight are removed (Beacons are always shown), unseen squares show the last height `team`
 * saw, and enemy actions `team` could not see are redacted from the log.
 */
export function viewFor(state: GameState, team: Team): GameState {
  if (!state.config.fog) return state;
  const vision = visionFor(state, team);
  const memory = state.memory?.[team] ?? state.heights;
  const heights = cloneHeights(memory);
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      const row = heights[r];
      if (row && isSeen(vision, r, c)) row[c] = heightAt(state.heights, r, c);
    }
  }
  const pieces = state.pieces.filter(
    (p) => p.team === team || !p.alive || p.type === 'B' || isSeen(vision, p.r, p.c),
  );
  return {
    ...state,
    heights,
    pieces,
    log: state.log.map((entry) => redact(entry, team, state.pieces)),
    positions: {},
    memory: null,
  };
}

function redact(entry: LogEntry, team: Team, pieces: readonly Piece[]): LogEntry {
  if (entry.hiddenFrom !== team) return entry;
  const lostOwnPiece = pieces.some((p) => p.id === entry.captured && p.team === team);
  return {
    ply: entry.ply,
    moveNo: entry.moveNo,
    team: entry.team,
    pieceId: '',
    action: { kind: 'move', r: -1, c: -1 },
    notation: '?',
    hiddenFrom: team,
    ...(lostOwnPiece && entry.captured !== undefined ? { captured: entry.captured } : {}),
  };
}

/**
 * Turns an action chosen from the mover's view into what really happens on the board.
 * Returns `null` when the piece is blocked and stays where it is (the turn is still spent).
 *
 * - Ending on a hidden enemy captures it.
 * - A Climber stops in front of whatever blocks its path (a hidden piece, or terrain that changed
 *   out of sight); if that is the first square, it stays put.
 * - Any other move or build that the real terrain or a hidden piece makes impossible is blocked.
 */
export function resolveIntent(state: GameState, piece: Piece, intended: Action): Action | null {
  const real = legalActions(state, piece.id);
  const exact = real.find((a) => {
    if (a.r !== intended.r || a.c !== intended.c) return false;
    if (intended.kind === 'build') return a.kind === 'build' && a.delta === intended.delta;
    return a.kind === 'move' || a.kind === 'capture';
  });
  if (exact) return exact;
  if (piece.type !== 'C' || intended.kind === 'build') return null;

  // The Climber goes as far as it really can along its line, without capturing on the way.
  const dr = Math.sign(intended.r - piece.r);
  const dc = Math.sign(intended.c - piece.c);
  const distance = Math.max(Math.abs(intended.r - piece.r), Math.abs(intended.c - piece.c));
  for (let d = distance - 1; d >= 1; d--) {
    const r = piece.r + dr * d;
    const c = piece.c + dc * d;
    const stop = real.find((a) => a.kind === 'move' && a.r === r && a.c === c);
    if (stop) return stop;
  }
  return null;
}

/** Each team's remembered heights after an action: every square it now sees is refreshed. */
export function updateMemory(
  state: GameState,
  memory: Record<Team, number[][]>,
  revealed: { team: Team; r: number; c: number } | null,
): Record<Team, number[][]> {
  const next = { I: cloneHeights(memory.I), B: cloneHeights(memory.B) };
  for (const team of ['I', 'B'] as const) {
    const vision = visionFor(state, team);
    for (let r = 0; r < SIZE; r++) {
      for (let c = 0; c < SIZE; c++) {
        const row = next[team][r];
        if (row && isSeen(vision, r, c)) row[c] = heightAt(state.heights, r, c);
      }
    }
  }
  if (revealed) {
    const row = next[revealed.team][revealed.r];
    if (row) row[revealed.c] = heightAt(state.heights, revealed.r, revealed.c);
  }
  return next;
}

/**
 * Whether `observer` could see an action: the mover's square before it, or the square acted on
 * or the mover's square after it.
 */
export function actionSeenBy(
  before: GameState,
  after: GameState,
  observer: Team,
  moverId: string,
  target: { r: number; c: number },
): boolean {
  const start = before.pieces.find((p) => p.id === moverId);
  const end = after.pieces.find((p) => p.id === moverId);
  const visionBefore = visionFor(before, observer);
  const visionAfter = visionFor(after, observer);
  return (
    (start !== undefined && isSeen(visionBefore, start.r, start.c)) ||
    isSeen(visionAfter, target.r, target.c) ||
    (end !== undefined && end.alive && isSeen(visionAfter, end.r, end.c))
  );
}
